'use client';

import type { LucideIcon } from 'lucide-react';
import * as React from 'react';

import { ConfirmationDialog, type ConfirmationDialogVariant } from '../components/confirmation-dialog';

type ConfirmOptions = {
  title: string;
  description: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmationDialogVariant;
  icon?: LucideIcon;
};

type ConfirmationContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmationContext = React.createContext<ConfirmationContextValue | null>(null);

const ConfirmationDialogProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null);
  const resolveRef = React.useRef<((value: boolean) => void) | null>(null);

  const confirm = React.useCallback((opts: ConfirmOptions): Promise<boolean> => {
    setOptions(opts);
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = React.useCallback(() => {
    resolveRef.current?.(true);
    resolveRef.current = null;
    setIsOpen(false);
  }, []);

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      resolveRef.current?.(false);
      resolveRef.current = null;
    }
    setIsOpen(nextOpen);
  }, []);

  const contextValue = React.useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmationContext value={contextValue}>
      {children}
      {options && (
        <ConfirmationDialog
          open={isOpen}
          onOpenChange={handleOpenChange}
          title={options.title}
          description={options.description}
          confirmText={options.confirmText}
          cancelText={options.cancelText}
          variant={options.variant}
          icon={options.icon}
          onConfirm={handleConfirm}
        />
      )}
    </ConfirmationContext>
  );
};

const useConfirmation = (): ConfirmationContextValue => {
  const context = React.use(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationDialogProvider');
  }
  return context;
};

export type { ConfirmOptions };
export { ConfirmationDialogProvider, useConfirmation };
